import React, {useEffect, useRef, useState} from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {getCountryCallingCode, isValidPhoneNumber} from 'libphonenumber-js';
import BookingSteps from '../BookingSteps';
import CampSteps from '../CampSteps/CampSteps';
import {BASE_URL_WEB} from '../../utility/constants';
import {AxiosHelperPortal, validateEmail} from '../../utility/helperFunctions';
import {isBlankQuestionAnswer} from '../../utility/questionnaireFieldUtils';
import RegistrationTimeField, {timeAnswerError} from './TimeField';
import './styles.scss';

const optionValue = option =>
  typeof option === 'object' && option !== null
    ? option.value ?? option.label ?? option.name
    : option;
const optionLabel = option =>
  typeof option === 'object' && option !== null
    ? option.label ?? option.value ?? option.name
    : option;

const normalizedPhone = value => String(value ?? '').replace(/[\s().-]/g, '');
const validPhoneAnswer = value => /^\+?\d+$/.test(normalizedPhone(value))
  && (isValidPhoneNumber(String(value), 'US')
    || (!String(value).startsWith('+') && isValidPhoneNumber(`+${value}`)));
const phoneInputValue = value => {
  const normalized = normalizedPhone(value);
  if (!normalized || normalized.startsWith('+')) return normalized;
  return `+${normalized.startsWith('1') || normalized.length > 10 ? '' : '1'}${normalized}`;
};
const countryCallingCode = country => {
  const iso = country.iso2 || country.countryCode;
  // The phone control may include an area code in dialCode (for example 1202).
  return iso ? getCountryCallingCode(iso.toUpperCase()) : country.dialCode;
};

export const loadServiceRegistration = async context => {
  const response = await AxiosHelperPortal(
    `${BASE_URL_WEB}service-registration/requirements?`,
    'post',
    context
  );
  if (!Array.isArray(response.data?.requirements))
    throw new Error('Unable to load service registration forms. Please try again.');
  return response.data.requirements;
};

const emailAnswerError = (question, value) => {
  if (value == null || String(value).trim() === '') return question.required ? 'This field is required' : '';
  return typeof value === 'string' && validateEmail(value) ? '' : 'Please enter a valid email address';
};

function RegistrationEmailField({question, inputProps}) {
  const [touched, setTouched] = useState(false);
  const message = touched ? emailAnswerError(question, inputProps.value) : '';
  const errorId = `${inputProps.id}-error`;
  const describedBy = [inputProps['aria-describedby'], message ? errorId : null].filter(Boolean).join(' ') || undefined;
  return <>
    <input {...inputProps} type="email" className={`${inputProps.className}${message ? ' is-invalid' : ''}`}
      aria-invalid={message ? true : undefined} aria-describedby={describedBy}
      onChange={event => { setTouched(true); inputProps.onChange(event); }} onBlur={() => setTouched(true)} />
    {message && <div id={errorId} className="invalid-feedback">{message}</div>}
  </>;
}

export function RegistrationField({question, value, onChange, memberId}) {
  const id = `registration-${question.sk}`;
  const selectedOptions = Array.isArray(value) ? value : value ? [value] : [];
  const compoundValue = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const props = {
    id,
    className: 'form-control',
    required: question.required,
    value: value ?? '',
    onChange: event => onChange(event.target.value),
    'aria-describedby': question.helper ? `${id}-help` : undefined,
  };
  if (question.field === 'email') return <RegistrationEmailField key={memberId} question={question} inputProps={props} />;
  if (question.field === 'time') return <RegistrationTimeField key={memberId} question={question}
    value={value} onChange={onChange} id={id} />;
  if (question.field === 'number') return <PhoneInput
    country="us" preferredCountries={['us', 'in']} enableAreaCodes
    value={phoneInputValue(value)}
    inputClass="form-control"
    placeholder={question.helper || 'Enter phone number'}
    inputProps={{id, name: question.sk, required: question.required, 'aria-describedby': props['aria-describedby']}}
    containerStyle={{width: '100%'}}
    inputStyle={{width: '100%', height: '38px', fontSize: '14px'}}
    isValid={(phone, country) => !phone || phone === countryCallingCode(country) || validPhoneAnswer(phone)
      || 'Please enter a valid phone number'}
    onChange={(phone, country) => onChange(phone && phone !== countryCallingCode(country) ? `+${phone}` : '')}
  />;
  if (question.field === 'dropdown')
    return (
      <select {...props}>
        <option value="">Select an option</option>
        {(question.ddVal || []).map((option, index) => (
          <option key={index} value={optionValue(option)}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
    );
  if (question.field === 'multiple-response')
    return (
      <div role="group" aria-labelledby={`${id}-label`}>
        {(question.ddVal || []).map((option, index) => (
          <label key={index} className="registration-choice">
            <input
              type="checkbox"
              checked={selectedOptions.includes(optionValue(option))}
              onChange={event =>
                onChange(
                  event.target.checked
                    ? [...selectedOptions, optionValue(option)]
                    : selectedOptions.filter(item => item !== optionValue(option))
                )
              }
            />{' '}
            {optionLabel(option)}
          </label>
        ))}
      </div>
    );
  const compound = {
    address: [
      ['street', 'Street address'],
      ['city', 'City'],
      ['state', 'State / Province'],
      ['zip', 'ZIP / Postal code'],
    ],
    height: [
      ['feet', 'Feet'],
      ['inch', 'Inches'],
    ],
    exercise: [
      ['sets', 'Sets'],
      ['reps', 'Reps'],
      ['weight', 'Weight'],
      ['notes', 'Notes'],
    ],
  };
  if (compound[question.field])
    return (
      <div className="row">
        {compound[question.field].map(([key, label]) => (
          <div className="col-sm-6 mb-2" key={key}>
            <label htmlFor={`${id}-${key}`}>{label}</label>
            <input
              {...props}
              id={`${id}-${key}`}
              value={compoundValue[key] ?? ''}
              required={question.required && key !== 'notes'}
              type={question.field === 'address' || key === 'notes' ? 'text' : 'number'}
              min={question.field === 'address' ? undefined : 0}
              max={key === 'inch' ? 11 : undefined}
              onChange={event => onChange({...compoundValue, [key]: event.target.value})}
            />
          </div>
        ))}
      </div>
    );
  const types = {date: 'date', numeric: 'number'};
  return (
    <input
      {...props}
      type={types[question.field] || 'text'}
      step={question.field === 'numeric' ? 'any' : undefined}
    />
  );
}

const hasAnswer = value => {
  if (value && typeof value === 'object' && 'format' in value) return !isBlankQuestionAnswer(value);
  if (value == null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'object') return Object.values(value).some(hasAnswer);
  return true;
};

export default function ServiceRegistration({
  context,
  requirements: initialRequirements,
  onComplete,
  onCancel,
  completionLabel = 'Continue to booking',
  isAppointment = false,
}) {
  const [requirements, setRequirements] = useState(initialRequirements);
  const [page, setPage] = useState(0);
  const [answers, setAnswers] = useState(() => requirements.map(item => ({...item.prefill})));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);
  const current = requirements[page];
  const next = async event => {
    event.preventDefault();
    setError('');
    const invalidTime = current.questions.find(question => question.field === 'time'
      && timeAnswerError(question, answers[page][question.sk]));
    if (invalidTime) {
      setError(`${invalidTime.q || invalidTime.name}: ${timeAnswerError(invalidTime, answers[page][invalidTime.sk])}`);
      return;
    }
    const invalidEmail = current.questions.find(question => question.field === 'email'
      && emailAnswerError(question, answers[page][question.sk]));
    if (invalidEmail) {
      setError(`${invalidEmail.q || invalidEmail.name}: ${emailAnswerError(invalidEmail, answers[page][invalidEmail.sk])}`);
      return;
    }
    const invalidPhone = current.questions.find(question => question.field === 'number'
      && (hasAnswer(answers[page][question.sk]) ? !validPhoneAnswer(answers[page][question.sk]) : question.required));
    if (invalidPhone) {
      setError(`Enter a valid phone number for ${invalidPhone.q || invalidPhone.name}.`);
      return;
    }
    if (
      current.questions.some(
        question =>
          question.required &&
          question.field === 'multiple-response' &&
          !answers[page][question.sk]?.length
      )
    ) {
      setError('Please answer all required questions.');
      return;
    }
    if (page < requirements.length - 1) {
      setPage(page + 1);
      return;
    }
    setSaving(true);
    try {
      const response = await AxiosHelperPortal(
        `${BASE_URL_WEB}service-registration/responses?`,
        'post',
        {
          ...context,
          responses: requirements.map((item, index) => ({
            memberId: item.memberId,
            formId: item.formId,
            version: item.version,
            answers: answers[index],
          })),
        }
      );
      if (!isMounted.current) return;
      if (!response.data?.registrationToken)
        throw new Error('Unable to save registration answers.');
      await onComplete(response.data.registrationToken);
    } catch (failure) {
      if (!isMounted.current) return;
      if (failure.response?.status === 409) {
        try {
          const latest = await loadServiceRegistration(context);
          if (!isMounted.current) return;
          if (!latest.length) {
            onCancel();
            return;
          }
          setRequirements(latest);
          setAnswers(latest.map(item => ({...item.prefill})));
          setPage(0);
          setError('The registration form changed. Review the updated questions and continue.');
          return;
        } catch (reloadError) {
          if (!isMounted.current) return;
          setError(
            reloadError.response?.data?.message ||
              'Unable to reload the registration form. Please try again.'
          );
          return;
        }
      }
      setError(
        Object.values(failure.response?.data?.errors || {})
          .flat()
          .join(' ') ||
          failure.response?.data?.message ||
          failure.message
      );
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };
  const update = (key, value) =>
    setAnswers(previous =>
      previous.map((item, index) => (index === page ? {...item, [key]: value} : item))
    );
  return (
    <main
      className={`service-registration-client page-content container${
        isAppointment ? ' service-registration-client--appointment' : ''
      }`}
    >
      <header className="registration-page-header">
        <h1>Service Registration Form</h1>
      </header>
      {isAppointment ? (
        <BookingSteps step={4} />
      ) : (
        <div className="service-registration-progress">
          <CampSteps current={3} showPayment={completionLabel !== 'Confirm booking'} />
        </div>
      )}
      <section className="registration-card" aria-label="Member registration">
        {page === 0 && current.intro && (
          <aside className="service-registration-intro">
            <div dangerouslySetInnerHTML={{__html: current.intro}} />
          </aside>
        )}
        {requirements.length > 1 && (
          <p className="service-registration-member-count text-muted">
            Member {page + 1} of {requirements.length}
          </p>
        )}
        <div className="registration-member-heading">
          <span className="registration-avatar" aria-hidden="true">
            {current.memberName
              .split(/\s+/)
              .filter(Boolean)
              .map(part => part[0])
              .slice(0, 2)
              .join('')}
          </span>
          <h2>
            Please complete the following information for <strong>{current.memberName}</strong>:
          </h2>
        </div>
        <form onSubmit={next}>
          <fieldset disabled={saving}>
            {current.questions.map(question => {
              const previousAnswers = answers
                .slice(0, page)
                .reverse()
                .find(memberAnswers => hasAnswer(memberAnswers[question.sk]));
              return (
                <div className="form-group" key={question.sk}>
                  <label
                    id={`registration-${question.sk}-label`}
                    htmlFor={`registration-${question.sk}`}
                  >
                    {question.q || question.name}{' '}
                    {question.required && (
                      <span className="registration-required">Required</span>
                    )}
                  </label>
                  <RegistrationField
                    memberId={current.memberId}
                    question={question}
                    value={answers[page][question.sk]}
                    onChange={value => update(question.sk, value)}
                  />
                  {question.helper && (
                    <small id={`registration-${question.sk}-help`} className="form-text text-muted">
                      {question.helper}
                    </small>
                  )}
                  {previousAnswers && (
                    <button
                      type="button"
                      className="btn btn-link registration-copy"
                      onClick={() =>
                        update(
                          question.sk,
                          JSON.parse(JSON.stringify(previousAnswers[question.sk]))
                        )
                      }
                    >
                      Same as previous member
                    </button>
                  )}
                </div>
              );
            })}
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            <div className="service-registration-actions d-flex justify-content-between mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => (page > 0 ? setPage(page - 1) : onCancel())}
              >
                Back
              </button>
              <button type="submit" className="btn btn-danger">
                {saving
                  ? 'Saving…'
                  : page < requirements.length - 1
                  ? 'Next member'
                  : completionLabel}
              </button>
            </div>
          </fieldset>
        </form>
      </section>
    </main>
  );
}
